import { useContext, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FailureTypeButtons } from "./FailureTypesButtons/FailureTypeButtons.view";
import { FailureAreasButtons } from "./FailureAreasButtons/FailureAreasButtons.view";
import { FailureFunctionalLocationsButtons } from "./FailureFunctionalLocationsButtons/FailureFunctionalLocationsButtons.view";
import { FailureCodesButtons } from "./FailureCodesButtons/FailureCodesButtons.view";
import { FailureDetailsSelections } from "./FailureDetailsSelections/FailureDetailsSelections.view";
import { DowntimeSaved } from "./DowntimeSaved/DowntimeSaved.view";
import { BackButton } from "../../../../components";
import { ConfirmAndSaveButton } from "../../../../components/ConfirmAndSaveButton/ConfirmAndSaveButton.view";
import { BreadcrumbsList } from "./BreadcrumbsList/BreadcrumbsList.view";
import { HMIContext } from "../../../../contexts/HMIContext/HMIContext";
import { MachineCenterContext } from "../../../../contexts/MachineCenterContext/MachineCenterContext";
import Box from "@mui/material/Box";
import { useStyles } from "./FailureDetails.styles";
import { Typography } from "@mui/material";
import {
    getCurrentStartDowntime,
    removeCurrentStartDowntime,
    isUptimeActive,
    setUptime,
    removeEventReportEditing,
    removeEventReportEditData,
    resetFailureDetails,
    removeCurrentStartBelowTriggerDowntimeCount,
    getUuidLocalserverDowntimeCode,
    removeUuidLocalserverDowntimeCode,
    getNotSelectedOrBelowTriggerEventReportId,
    removeNotSelectedOrBelowTriggerEventReportId,
    getClusterIdHMI,
    getUuidLocalserverDowntimeCodeDuringInput,
    removeUuidLocalserverDowntimeCodeDuringInput,
    resetLocalStorage,
} from "../../../../utilities/storageUtilities";
import {
    FAILUREDETAILSSTEPS,
    MEASURE_METRICS,
    DOWNTIMEMETHODCAPTURE,
    IconsTrafficLightStatusCodes,
    LocalStorageGlobalKeys,
    LocalStorageCachedDataKeysCluster,
    LocalStorageCachedDataKeysHMI,
} from "../../../../constants/constants";
import { HMICachedDataContext } from "../../../../contexts/HMICachedDataContext/HMICachedDataContext";
import { GoToHomeScreenButton } from "components/GoToHomeScreenButton/GoToHomeScreenButton.view";
import { MQTTBrokerContext } from "contexts/MQTTBrokerContext/MQTTBrokerContext";
import { IArea, IFunctionalLocation } from "./FailureDetailsInterfaces";
import { ViewOptionsEnum } from "components/ViewDropdown/ViewDropdown.view";
import { ClusterContext } from "contexts/ClusterContext/ClusterContext";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";
import useUptimeDowntimeActionsHMI from "hooks/useUptimeDowntimeActionsHMI";
import { IMachineCentersClusterStatus } from "contexts/ClusterContext/ClusterContextInterfaces";
import { IEventsEditionsPending } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";

export const FailureDetails = () => {
    const { t } = useTranslation();
    const { store, dispatch } = useContext(HMICachedDataContext);
    const { publishEventReport, isBrokerConnected, isLocalServerActive } = useContext(MQTTBrokerContext);
    const {
        failureType,
        area,
        functionalLocation,
        failureCode,
        failureDetailsStep,
        notes,
        operator,
        handlePreviousFailureDetailsStep,
        handleNextFailureDetailsStep,
        handleSetFailureDetailStep,
        handleView,
        handleNotes,
        resetEditingSelections,
        resetHMIContextProvider,
    } = useContext(HMIContext);
    const {
        handleMachineStatus,
        machineStatus,
        handleDowntimeCounter,
        plc,
        handleContinueDowntimeDetailsInput,
        continueDowntimeDetailsInput,
        handleResetMachineCenterContext,
    } = useContext(MachineCenterContext);
    const { machineCentersClusterStatus, handleSetMachineCentersClusterStatus } = useContext(ClusterContext);
    const { store: clusterStore } = useContext(ClusterCachedDataContext);
    const { handleClusterLogin, handleMachineActiveUptime } = useUptimeDowntimeActionsHMI();
    const [isSaving, setIsSaving] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    const isClusterAllowed = useMemo(
        () =>
            store.machineCenter?.production_line?.mill?.cluster_enabled ||
            clusterStore.machineCentersCluster[0]?.production_line?.mill?.cluster_enabled,
        [clusterStore, store],
    );
    const isCluster = useMemo(
        () => Boolean(getClusterIdHMI()) && isClusterAllowed && Boolean(clusterStore.machineCentersCluster),
        [isClusterAllowed, clusterStore],
    );
    const classes = useStyles();
    const isPlc = useMemo(
        () => store.machineCenter?.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC && plc,
        [store.machineCenter?.downtime_method_capture_id, plc],
    );

    const filteredAreas: IArea[] = useMemo(() => {
        if (store.machineCenter && failureType) {
            const areasWithFilteredFunctionalLocations: IArea[] = store.machineCenter.areas
                .map((area) => {
                    const functionalLocationsWithType: IFunctionalLocation[] = area.functional_locations
                        .map((functionalLocation) => {
                            const filteredFailureCodes = failureType?.failure_codes!.filter((fc) =>
                                functionalLocation?.location_specific_functional_location_failure_codes?.some(
                                    (locationSpecificFC) => locationSpecificFC.failure_code_id === fc.failure_code_id,
                                ),
                            );

                            return {
                                ...functionalLocation,
                                failure_codes: filteredFailureCodes ? filteredFailureCodes.map((item) => item) : [],
                            };
                        })
                        .filter((functionalLocation) => {
                            return functionalLocation.failure_codes!.length > 0;
                        });
                    return {
                        ...area,
                        functional_locations: functionalLocationsWithType,
                    };
                })
                .filter((area) => {
                    return area.functional_locations.length > 0;
                });
            return areasWithFilteredFunctionalLocations;
        }
        return [];
    }, [store.machineCenter, failureType]);

    const filteredFailureCodesByFunctionalLocation = useMemo(() => {
        if (filteredAreas && filteredAreas.length > 0 && functionalLocation) {
            const filteredFailureCodes: IFunctionalLocation[] = [];

            filteredAreas.forEach((filteredArea) => {
                const failureCodesByFunctionalLocationId = filteredArea.functional_locations.filter(
                    (item) => item.functional_location_id === functionalLocation.functional_location_id,
                );
                filteredFailureCodes.push(...failureCodesByFunctionalLocationId);
            });

            return filteredFailureCodes;
        } else {
            return [];
        }
    }, [filteredAreas, functionalLocation]);

    const noLocalserverConnection = useMemo(
        () => !isBrokerConnected || !isLocalServerActive,
        [isBrokerConnected, isLocalServerActive],
    );

    const handleMovingToCluster = useCallback(
        async (currentStatusDetails: IMachineCentersClusterStatus[]) => {
            const localStorageKeyExceptions = [
                LocalStorageGlobalKeys.LANGUAGES,
                LocalStorageGlobalKeys.I18_LANGUAGE_SELECTION,
                LocalStorageGlobalKeys.TOKEN,
                ...Object.values(LocalStorageCachedDataKeysCluster),
                LocalStorageCachedDataKeysHMI.MQTT_BROKER_SETTINGS,
                LocalStorageCachedDataKeysHMI.DEFAULT_UPTIME_VALUES,
                LocalStorageCachedDataKeysHMI.OPERATOR,
                LocalStorageCachedDataKeysHMI.LAST_INTERNET_CONNECTION_RECOVERY_DATE,
                LocalStorageCachedDataKeysHMI.DATA_LAST_UPDATED,
            ];
            // The HMI is thought to work as a closed network offline first
            const isOnline = false;
            await handleClusterLogin(
                clusterStore.machineCentersCluster?.map((machineCenter) => ({
                    machine_center_id: machineCenter.machine_center_id,
                })),
                isOnline,
                currentStatusDetails,
            );
            resetLocalStorage(localStorageKeyExceptions);
            handleResetMachineCenterContext();
            resetHMIContextProvider();
            dispatch.resetStore();
        },
        [
            handleResetMachineCenterContext,
            resetHMIContextProvider,
            clusterStore.machineCentersCluster,
            handleClusterLogin,
            dispatch,
        ],
    );

    const handleUndoDowntime = useCallback(async () => {
        const uptime = isUptimeActive();
        handleMachineStatus(true);
        if (!uptime) {
            setUptime();
            await handleMachineActiveUptime();
            removeCurrentStartDowntime();
            handleDowntimeCounter(false);
            handleView(ViewOptionsEnum.summary);
            handleNotes("");
            removeUuidLocalserverDowntimeCode();
            removeUuidLocalserverDowntimeCodeDuringInput();
            removeNotSelectedOrBelowTriggerEventReportId();
        }
    }, [handleMachineStatus, handleDowntimeCounter, handleView, handleNotes, handleMachineActiveUptime]);

    const handleBackButton = () => {
        if (failureType?.is_location_specific) {
            if (failureCode! && failureDetailsStep === FAILUREDETAILSSTEPS.FAILUREDETAILSSELECTIONS) {
                handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURECODES);
            } else {
                handlePreviousFailureDetailsStep();
            }
        } else {
            handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
        }
    };

    const handleCancelButton = async () => {
        setIsCancelling(true);
        if (store.isEventReportEditing) {
            dispatch.isEventReportEditing(false);
            removeEventReportEditing();
            removeEventReportEditData();
            handleView(ViewOptionsEnum.details);
            resetFailureDetails();
            resetEditingSelections();
            handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
            if (isPlc && !machineStatus) {
                handleSetFailureDetailStep(FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME);
            }
        } else {
            await handleUndoDowntime();
        }
        handleContinueDowntimeDetailsInput(false);
        setIsCancelling(false);
    };

    const handleConfirmAndSave = useCallback(async () => {
        setIsSaving(true);
        const currentStartDowntime = getCurrentStartDowntime();
        const plcMachineCenter = store.machineCenter?.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC;
        const uuidLocalserverDowntimeCode = getUuidLocalserverDowntimeCode();
        const uuidLocalserverDowntimeCodeDuringInput = getUuidLocalserverDowntimeCodeDuringInput();
        const notSelectedOrBelowTriggerEventReportId = getNotSelectedOrBelowTriggerEventReportId();
        const clusterId = getClusterIdHMI();
        if (
            store.machineCenter &&
            Boolean(operator) &&
            failureType &&
            failureCode &&
            (currentStartDowntime || continueDowntimeDetailsInput)
        ) {
            try {
                if (
                    plcMachineCenter &&
                    (uuidLocalserverDowntimeCode ||
                        notSelectedOrBelowTriggerEventReportId ||
                        uuidLocalserverDowntimeCodeDuringInput)
                ) {
                    await publishEventReport({
                        ...(uuidLocalserverDowntimeCodeDuringInput || uuidLocalserverDowntimeCode
                            ? { uuid: uuidLocalserverDowntimeCodeDuringInput || uuidLocalserverDowntimeCode }
                            : { eventReportId: notSelectedOrBelowTriggerEventReportId }),
                        machineCenter: store.machineCenter.machine_center_id,
                        user: Number(operator),
                        ...(area && { area: area.area_id }),
                        ...(functionalLocation && { functionalLocation: functionalLocation.functional_location_id }),
                        failureType: failureType.failure_type_id,
                        failureCode: failureCode.failure_code_id,
                        ...(notes && { notes: notes }),
                    });
                } else {
                    if (currentStartDowntime)
                        await publishEventReport({
                            machineCenter: store.machineCenter.machine_center_id,
                            user: Number(operator),
                            ...(area && { area: area.area_id }),
                            ...(functionalLocation && {
                                functionalLocation: functionalLocation.functional_location_id,
                            }),
                            failureType: failureType.failure_type_id,
                            failureCode: failureCode.failure_code_id,
                            startDate: new Date(currentStartDowntime).toISOString(),
                            measureMetric: MEASURE_METRICS.DOWNTIME,
                            downtimeMethodCapture: isPlc ? DOWNTIMEMETHODCAPTURE.PLC : DOWNTIMEMETHODCAPTURE.MANUAL,
                            ...(notes && { notes: notes }),
                            ...store.machineCenter,
                            ...(isClusterAllowed &&
                                clusterId && {
                                    clusterId: clusterId,
                                }),
                        });
                }
            } catch (error) {
                setIsSaving(false);
                return;
            }
            if (uuidLocalserverDowntimeCode) removeUuidLocalserverDowntimeCode();
            if (notSelectedOrBelowTriggerEventReportId) removeNotSelectedOrBelowTriggerEventReportId();
            if (uuidLocalserverDowntimeCodeDuringInput) removeUuidLocalserverDowntimeCodeDuringInput();
            removeCurrentStartBelowTriggerDowntimeCount();
            handleNextFailureDetailsStep();
            handleContinueDowntimeDetailsInput(false);
            if (isCluster) {
                const currentClusterMachinesStatusLocalstorage = machineCentersClusterStatus.map(
                    (currentMachineCenterClusterStatus) => {
                        if (
                            currentMachineCenterClusterStatus.machineCenterId === store.machineCenter?.machine_center_id
                        ) {
                            return {
                                ...currentMachineCenterClusterStatus,
                                machinePlcDisconnected:
                                    store.machineCenter.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC
                                        ? !isPlc
                                        : false,
                                eventLocalServerUuid: undefined,
                                notSelectedOrBelowTriggerEventReportId: undefined,
                                status: IconsTrafficLightStatusCodes.DOWN,
                                downtimePending: false,
                                failureDetails: {
                                    failureType: failureType,
                                    failureCode: failureCode,
                                    ...(area && { area: area }),
                                    ...(functionalLocation && { functionalLocation: functionalLocation }),
                                },
                            };
                        } else {
                            return { ...currentMachineCenterClusterStatus };
                        }
                    },
                );
                handleSetMachineCentersClusterStatus(currentClusterMachinesStatusLocalstorage);
                await handleMovingToCluster(currentClusterMachinesStatusLocalstorage);
            } else {
                dispatch.GetData(store.machineCenter.machine_center_id);
            }
        }
        setIsSaving(false);
    }, [
        failureType,
        area,
        functionalLocation,
        failureCode,
        handleNextFailureDetailsStep,
        handleMovingToCluster,
        isPlc,
        notes,
        store.machineCenter,
        operator,
        dispatch,
        publishEventReport,
        handleSetMachineCentersClusterStatus,
        isCluster,
        machineCentersClusterStatus,
        isClusterAllowed,
        continueDowntimeDetailsInput,
        handleContinueDowntimeDetailsInput,
    ]);

    const handleConfirmAndSaveUpdatedEventReport = useCallback(async () => {
        setIsSaving(true);
        if (Boolean(operator) && store.isEventReportEditing && store.editEventReportData.row!) {
            try {
                await publishEventReport({
                    eventReportId: store.editEventReportData.row!.event_report_id,
                    ...(area && { area: area.area_id }),
                    ...(functionalLocation && { functionalLocation: functionalLocation.functional_location_id }),
                    failureType: failureType!.failure_type_id,
                    failureCode: failureCode!.failure_code_id,
                    ...(notes && { notes: notes }),
                    locationSpecific: failureType?.is_location_specific,
                    editedByUserId: Number(operator),
                });

                const eventUpdateDetails: IEventsEditionsPending[0] = {
                    machineCenterId: store.machineCenter?.machine_center_id!,
                    failureTypeDetails: failureType,
                    failureCodeDetails: failureCode,
                    ...(area && { areaDetails: area }),
                    ...(functionalLocation && { functionalLocationDetails: functionalLocation }),
                    lastUpdateDate:
                        store?.editEventReportData?.row?.updated_at || store?.editEventReportData?.row?.created_at,
                };
                dispatch.trackEventEdition(store.editEventReportData.row!.event_report_id, eventUpdateDetails);
            } catch (error) {
                setIsSaving(false);
                return;
            }
        }
        dispatch.isEventReportEditing(false);
        removeEventReportEditing();
        removeEventReportEditData();
        handleView(ViewOptionsEnum.details);
        resetFailureDetails();
        resetEditingSelections();
        handleNotes("");
        if (isPlc && !machineStatus) {
            handleSetFailureDetailStep(FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME);
        }
        setIsSaving(false);
    }, [
        area,
        failureCode,
        failureType,
        functionalLocation,
        notes,
        operator,
        dispatch,
        isPlc,
        resetEditingSelections,
        handleView,
        store,
        machineStatus,
        handleSetFailureDetailStep,
        handleNotes,
        publishEventReport,
    ]);

    const handleBackToHomeScreenPLC = useCallback(() => {
        handleSetFailureDetailStep(FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME);
    }, [handleSetFailureDetailStep]);

    return (
        <Box className={classes.failureDetailsContainer}>
            {failureDetailsStep < FAILUREDETAILSSTEPS.DOWNTIMESAVED && (
                <Box className={classes.failureDetailsButtonsContainer}>
                    {failureDetailsStep < FAILUREDETAILSSTEPS.FAILUREDETAILSSELECTIONS &&
                        !store.isEventReportEditing && (
                            <BreadcrumbsList
                                failureDetailsStep={failureDetailsStep}
                                failureType={failureType}
                                area={area}
                                functionalLocation={functionalLocation}
                                failureCode={failureCode}
                            />
                        )}

                    {failureDetailsStep === FAILUREDETAILSSTEPS.FAILURETYPES && (
                        <Box className={classes.buttonsBox}>
                            <Typography variant="h1" className={classes.failureDetailsTitle}>
                                {t("HMI.failure_details.failure_types.title")}
                            </Typography>
                            {store.failureTypes && <FailureTypeButtons failureTypes={store.failureTypes} />}
                        </Box>
                    )}
                    {failureType?.is_location_specific && (
                        <>
                            {failureDetailsStep === FAILUREDETAILSSTEPS.AREAS && failureType && store.machineCenter && (
                                <Box className={classes.buttonsBox}>
                                    <Typography variant="h1" className={classes.failureDetailsTitle}>
                                        {t("HMI.failure_details.area")}
                                    </Typography>
                                    {<FailureAreasButtons areas={filteredAreas!} />}
                                </Box>
                            )}
                            {failureDetailsStep === FAILUREDETAILSSTEPS.FUNCTIONALLOCATION &&
                                area &&
                                area.functional_locations &&
                                store.machineCenter && (
                                    <Box className={classes.buttonsBox}>
                                        <Typography variant="h1" className={classes.failureDetailsTitle}>
                                            {t("HMI.failure_details.functional_location")}
                                        </Typography>
                                        {
                                            <FailureFunctionalLocationsButtons
                                                functionalLocations={area.functional_locations}
                                            />
                                        }
                                    </Box>
                                )}
                        </>
                    )}
                    {failureDetailsStep === FAILUREDETAILSSTEPS.FAILURECODES &&
                        failureType &&
                        failureType!.failure_codes &&
                        store.machineCenter && (
                            <Box className={classes.buttonsBox}>
                                <Typography variant="h1" className={classes.failureDetailsTitle}>
                                    {t("HMI.failure_details.failure_codes")}
                                </Typography>
                                {
                                    <FailureCodesButtons
                                        failureCodes={
                                            failureType.is_location_specific && filteredAreas.length > 0
                                                ? filteredFailureCodesByFunctionalLocation[0].failure_codes
                                                : failureType!.failure_codes
                                        }
                                    />
                                }
                            </Box>
                        )}
                    {failureDetailsStep === FAILUREDETAILSSTEPS.FAILUREDETAILSSELECTIONS &&
                        ((failureType && failureCode && store.machineCenter) ||
                            (store.isEventReportEditing && store.editEventReportData.row)) && (
                            <Box className={classes.buttonsBox}>
                                {store.isEventReportEditing && store.editEventReportData.row! ? (
                                    <Typography variant="h1" className={classes.failureDetailsTitle}>
                                        {t("HMI.details.edit_event_report.title")}
                                    </Typography>
                                ) : (
                                    <Typography variant="h1" className={classes.failureDetailsTitle}>
                                        {t("HMI.failure_details.selections.title")}
                                    </Typography>
                                )}

                                {
                                    <FailureDetailsSelections
                                        failureType={failureType}
                                        area={area}
                                        functionalLocation={functionalLocation}
                                        failureCode={failureCode}
                                        notes={notes}
                                    />
                                }
                            </Box>
                        )}
                </Box>
            )}
            {failureDetailsStep === FAILUREDETAILSSTEPS.DOWNTIMESAVED &&
                failureType &&
                failureCode &&
                store.machineCenter && (
                    <>
                        {
                            <DowntimeSaved
                                failureType={failureType!}
                                area={area}
                                functionalLocation={functionalLocation}
                                failureCode={failureCode!}
                                notes={notes}
                            />
                        }
                    </>
                )}
            {failureDetailsStep !== FAILUREDETAILSSTEPS.DOWNTIMESAVED &&
                failureDetailsStep !== FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME && (
                    <Box className={classes.failureDetailsButtonsMenu}>
                        <Box className={classes.navigationButtonsContainer}>
                            {((store.isEventReportEditing && isPlc) ||
                                store.machineCenter?.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.MANUAL ||
                                (store.machineCenter?.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC &&
                                    (!plc || continueDowntimeDetailsInput))) && (
                                <BackButton
                                    disabled={isSaving || isCancelling}
                                    name={"HMI.failure_details.cancel_button"}
                                    icon={"cancel"}
                                    handleClick={handleCancelButton}
                                />
                            )}
                            {failureDetailsStep > FAILUREDETAILSSTEPS.FAILURETYPES &&
                                failureDetailsStep < FAILUREDETAILSSTEPS.FAILUREDETAILSSELECTIONS && (
                                    <BackButton
                                        disabled={isSaving || isCancelling}
                                        name={"HMI.failure_details.back_button"}
                                        icon={"back"}
                                        handleClick={handleBackButton}
                                    />
                                )}
                        </Box>
                        {failureDetailsStep === FAILUREDETAILSSTEPS.FAILUREDETAILSSELECTIONS && (
                            <ConfirmAndSaveButton
                                disabled={isSaving || isCancelling || noLocalserverConnection}
                                operator={operator}
                                failureCode={failureCode}
                                handleClick={
                                    store.isEventReportEditing
                                        ? handleConfirmAndSaveUpdatedEventReport
                                        : handleConfirmAndSave
                                }
                            />
                        )}
                    </Box>
                )}
            {failureDetailsStep === FAILUREDETAILSSTEPS.DOWNTIMESAVED && isPlc && (
                <Box className={classes.goToHomeScreen}>
                    <GoToHomeScreenButton handleClick={handleBackToHomeScreenPLC} />
                </Box>
            )}
        </Box>
    );
};
